import React from 'react'

import { classList } from 'react-classlist-helper'

import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'
import { preventLastWordWrap } from '~/client/src/shared/utils/textUtils'

type IAction = {
  title: string
  onClick: () => void
}

type IActions =
  | [IAction | React.ReactNode]
  | [IAction | React.ReactNode, IAction | React.ReactNode]

interface IProps {
  open: boolean
  title?: React.ReactNode
  icon?: React.ReactNode
  onClose: () => void
  clickOutsideToDismiss?: boolean
  className?: string
  style?: React.CSSProperties

  // Actions can be either a title/callback pair, or a custom element each
  // One or two actions are allowed
  actions?: IActions

  children?: React.ReactNode
}

export default class CleanDialog extends React.Component<IProps> {
  private closeOverlay: (() => void) | null = null

  public componentDidMount() {
    if (this.props.open) {
      this.closeOverlay = notifyOverlayOpen()
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (!prevProps.open && this.props.open) {
      this.closeOverlay = notifyOverlayOpen()
    } else if (prevProps.open && !this.props.open) {
      this.closeOverlay?.()
      this.closeOverlay = null
    }
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
  }

  public render() {
    const {
      open,
      title,
      icon,
      onClose,
      clickOutsideToDismiss = false,
      className,
      style,
      actions,
      children,
    } = this.props

    const formattedTitle =
      typeof title === 'string' ? preventLastWordWrap(title) : title

    return (
      <div
        className={classList({
          'tw:absolute tw:left-0 tw:top-0 tw:w-screen tw:h-screen tw:z-50 tw:text-right tw:bg-black/50 tw:opacity-0 tw:transition-all tw:duration-200 tw:ease-out tw:h-full tw:flex tw:items-center tw:justify-center': true,
          'tw:opacity-100': open,
          [className]: !!className,
        })}
        onClick={() => clickOutsideToDismiss && onClose()}
        style={style}
      >
        <div
          className="tw:max-w-[80%] tw:rounded-[8px] tw:bg-background tw:border tw:border-solid tw:border-border tw:p-6 tw:pt-3"
          onClick={event => {
            event.stopPropagation()
          }}
        >
          <div className="tw:text-body-md tw:text-foreground">
            {icon && <div className="tw:mt-3">{icon}</div>}
            <h1 className="tw:font-sans tw:text-[24px] tw:font-normal tw:leading-[32px] tw:tracking-[-0.31px] tw:text-center tw:[word-break:auto-phrase]">
              {formattedTitle}
            </h1>
          </div>
          <div className="tw:text-body-md tw:text-foreground tw:text-left tw:my-5 tw:[&_*]:tw:text-foreground">
            {children}
          </div>
          {actions && (
            <div className="tw:flex tw:items-center tw:justify-end">
              {actions.map((action, i) => (
                <div
                  className={classList({
                    'tw:flex-1': true,
                    'tw:pl-4': i > 0,
                  })}
                  key={i}
                >
                  {this.isAction(action) ? (
                    <span
                      className="tw:self-end tw:text-primary tw:text-body-md"
                      onClick={action.onClick}
                    >
                      {action.title}
                    </span>
                  ) : React.isValidElement(action) ? (
                    action
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  private isAction = (action: unknown): action is IAction => {
    return (
      (action as IAction).title !== undefined &&
      (action as IAction).onClick !== undefined
    )
  }
}
